// ═══════════════════════════════════════════════════════════════════════════
// LA COTISATION DE FONDS DE SÉCURITÉ D'EXISTENCE — UN SEUL ENDROIT QUI LA CALCULE
//
// POURQUOI CE MODULE EXISTE. Le 25/08/2026, huit taux ont été repris du fichier
// des taux de l'ONSS. En les branchant, deux choses sont apparues :
//
//   1. **Le taux dépend du public.** CP 126 : 15,45 % pour un ouvrier, 0,23 %
//      pour un employé, qui relève du Fonds social de la CP 200. Servir l'un à
//      l'autre le multiplie par 67.
//   2. **La base ouvrière est à 108 %.** L'intitulé officiel du code 820 le dit
//      mot pour mot : « sur les rémunérations à 108 % des travailleurs manuels ».
//      Le calcul historique appliquait le taux au brut nu — 8 % sous-comptés.
//
// Et surtout : **31 fichiers lisent `fondsSE.taux_patronal` directement**
// (19 moteurs sectoriels, 12 écrans). Corriger le seul moteur de paie aurait
// refait la faute déjà commise quatre fois de suite — réparer une surface et
// laisser les autres mentir. D'où ce module : la règle vit ici, une fois.
//
// ⚠️ LES DEUX CORRECTIONS CI-DESSUS NE S'APPLIQUENT QU'AUX TAUX SOURCÉS ONSS
// (`onssSourcee`). Sans ce drapeau, elles rendent exactement ce que le code
// rendait avant : taux unique, base 100 %. La règle est juste, mais l'appliquer
// aux taux ronds jamais sourcés les rendrait différents sans les rendre plus
// justes — et pour plusieurs, plus BAS. Un sous-comptage se découvre après coup,
// chez le client.
//
// 🔑 TROISIÈME CORRECTION, AJOUTÉE LE 25/08/2026 : **la catégorie d'employeur**.
// Elle, à l'inverse, ne dépend PAS de `onssSourcee` — et c'est délibéré. Quand
// le dossier dit de quelle catégorie ONSS relève l'employeur, la question n'a
// plus qu'une réponse et c'est le percepteur qui la donne : préférer un nombre
// rond maison à la réponse de l'ONSS à la question exacte n'aurait pas de sens.
// Le détail, avec les écarts mesurés, est en tête de `tauxFondsSE`.
//
// Sources : ONSS, fichier des taux DmfA 2026/3 · annexe structurée n° 2 pour
// l'intitulé des codes travailleur · tableau FSE_3-2026.xlsx pour le recoupement.
// Le détail vit dans `app/lib/onss-fse-parse.js`.
// ═══════════════════════════════════════════════════════════════════════════

import { tauxPourCategorie, tauxPourcentage, cleOnssPourCp } from './onss-fse-officiel.js';
import { categorieEmployeurUtilisable } from './categorie-employeur.js';

/** Arrondi financier maison (R9). */
const r2 = (n) => Math.round(n * 100) / 100;

/**
 * ⚠️ LA BASE 108 % ÉTAIT ÉCRITE EN TOUTES LETTRES ET LE CALCUL NE LA VOYAIT PAS.
 *
 * Le drapeau s'appelle `base108Ouvrier` et n'était lu que sur les fiches
 * `onssSourcee`. Or **six fiches sur 174** disent leur assiette dans une phrase,
 * sous `assiette` ou `base`, sans porter le drapeau :
 *
 *   CP 124  « brut à 108 % (art. 7 : base du pécule de vacances) » — 10,67 %
 *   CP 140  « 108 »                                                —  7,70 %
 *   CP 125.02 / 125.03 « salaires bruts a 108 % »                  — 10,47 / 10,28 %
 *   CP 322.01 « brut ONSS à 108 % »                                —  6,99 %
 *   CP 149.01 « brut à 108 % »                                     —  1,10 %
 *
 * Sur un brut de 3 000 €, la CP 124 sous-comptait donc **25,61 € par mois et
 * par travailleur**. C'est le même piège que partout ailleurs cette semaine :
 * la donnée est là, écrite et sourcée, et le code cherche un autre nom de champ.
 *
 * ⛔ ON NE LIT PAS N'IMPORTE QUEL 108. Uniquement `assiette` et `base`, qui ne
 * servent qu'à ça — pas `note`, où « 108 » pourrait être un numéro de CCT. Et
 * uniquement pour le public OUVRIER : la base majorée est celle des
 * travailleurs manuels (intitulé ONSS du code 820).
 *
 * ⛔ ET LE GATE `sourceOnss` NE S'APPLIQUE PAS ICI, délibérément : il existe
 * pour ne pas changer un TAUX rond non sourcé. Ici on ne touche pas au taux, on
 * applique l'assiette que la fiche DÉCLARE — une fiche qui écrit « brut à
 * 108 % » a été lue sur ce point.
 */
const DIT_108 = /\b108\b/;
function baseA108(f, ouvrier, sourceOnss) {
  if (!ouvrier || !f) return false;
  if (sourceOnss && f.base108Ouvrier) return true;
  return DIT_108.test(String(f.assiette ?? '')) || DIT_108.test(String(f.base ?? ''));
}

/** Base ONSS : 108 % du brut pour un ouvrier, le brut nu pour un employé. */
export const COEFF_OUVRIER = 1.08;

/**
 * 📐 TOUTES LES ASSIETTES MAJORÉES NE VALENT PAS 108 % — la CP 324 le montre.
 *
 * `baseA108` couvre le cas fréquent, celui du code ONSS 820 « sur les
 * rémunérations à 108 % des travailleurs manuels ». Mais les statuts du Fonds
 * pour l'industrie diamantaire (CCT du 03/10/2022, art. 21) écrivent que « les
 * rémunérations/salaires bruts sont déclarés à 110 % » — un dixième de plus, et
 * pour les deux publics. Sans ce champ, la cotisation de 2 % de cette CP se
 * calculerait sur le brut nu et sous-compterait de 10 %.
 *
 * ⛔ ON NE DEVINE PAS CE COEFFICIENT DANS UNE PHRASE. `baseA108` cherche « 108 »
 * dans `assiette`/`base` parce que ce nombre-là n'a qu'un sens dans ce contexte.
 * « 110 » n'a pas cette chance : un numéro de CCT, une CP, un article peuvent
 * l'écrire. Le coefficient se DÉCLARE donc en clair, dans un champ numérique
 * dédié, ou il ne s'applique pas.
 *
 * @returns {number} le coefficient à appliquer au brut avant le taux du fonds.
 */
function coeffDAssiette(f, ouvrier, sourceOnss) {
  const declare = Number(f?.coeffAssiette);
  if (Number.isFinite(declare) && declare > 0) return declare;
  return baseA108(f, ouvrier, sourceOnss) ? COEFF_OUVRIER : 1;
}

/**
 * ⚖️ QUELLE FICHE PORTE LE FONDS D'UNE (SOUS-)CP — UNE SEULE RÈGLE, DEUX APPELANTS.
 *
 * Cette règle vivait DANS `calcCotisationsSectorielles`, donc le moteur de paie
 * l'appliquait et l'écran de la fiche CP, lui, en appliquait une autre
 * (`ficheEffective`, qui sert la complétude et préfère toujours la valeur
 * propre). Résultat mesuré le 25/08/2026 sur un brut de 2 500 € : **24 couples
 * CP × public** où les deux ne donnaient pas le même montant — jusqu'à 85 €
 * d'écart sur la CP 149.01, dans les deux sens selon la CP.
 *
 * ⛔ ON DESCEND À LA SOUS-CP UNIQUEMENT SI SON TAUX EST PROUVÉ, c'est-à-dire
 * porté par un bloc `cctSourcee` (convention lue au texte primaire) ou
 * `onssSourcee` (le percepteur). Sinon on lit la mère. Ce n'est pas de la
 * timidité : sur les sous-CP à taux propre, la plupart des nombres sont ronds
 * et sans source des DEUX côtés — descendre les rendrait plus SPÉCIFIQUES sans
 * les rendre plus JUSTES, et pour plusieurs cela ferait BAISSER la cotisation
 * facturée. Un sous-comptage se découvre après coup, chez le client.
 *
 * ⚠️ À NE PAS CONFONDRE AVEC `ficheEffective` de `cp-sous-division.js`, qui
 * répond à une autre question — « l'app sait-elle répondre pour un employeur
 * codé ainsi ? » — et qui a raison de préférer la valeur propre pour le nom,
 * les congés ou la prime. Pour un TAUX facturé, la preuve prime la spécificité.
 *
 * @returns {{bloc: object|null, cle: string|null, sousCpRetenue: boolean}}
 */
export function blocFondsSE(cp, tauxSectoriels) {
  const t = tauxSectoriels || {};
  const cpBrut = String(cp ?? '').trim();
  if (!cpBrut) return { bloc: null, cle: null, sousCpRetenue: false };
  const cpMere = cpBrut.replace(/[^0-9]/g, '').slice(0, 3);
  const entreeSousCp = t[cpBrut] || null;
  // ⚠️ TROISIÈME PREUVE ADMISE LE 25/08/2026 : `ecartOnssExplique`.
  // Le critère ne connaissait que la CCT et le fichier ONSS. Résultat mesuré :
  // la CP 149.01 (électriciens) recevait le **4,50 % non sourcé de sa mère**
  // alors qu'elle porte **1,10 % lu au texte primaire** (CCT du 16/10/2023,
  // art. 29.2, statuts coordonnés du FSE Volta) — et que sa composition est
  // vérifiée CONTRE l'ONSS : 1,10 FSE + 0,80 formation + 13,15 prime de fin
  // d'année = 15,05 %, exactement ce que le percepteur encaisse sous le code
  // 820. Un bloc dont l'arithmétique retombe sur le montant perçu est au moins
  // aussi prouvé qu'un bloc recopié du fichier ONSS.
  // ⛔ Le garde-fou GARDE tout son sens pour les autres : il ne s'ouvre pas aux
  // nombres ronds, il s'ouvre à une preuve de plus. Un seul cas concerné sur
  // les 174 fiches, et c'est celui où la règle donnait le résultat inverse de
  // son intention.
  const prouvee = Boolean(
    entreeSousCp?.fondsSE?.cctSourcee
    || entreeSousCp?.fondsSE?.onssSourcee
    || entreeSousCp?.fondsSE?.ecartOnssExplique,
  );
  const cle = prouvee ? cpBrut : cpMere;
  const entree = prouvee ? entreeSousCp : (t[cpMere] || null);
  return { bloc: entree?.fondsSE || null, cle: entree ? cle : null, sousCpRetenue: prouvee };
}

/**
 * ⚖️ LA CATÉGORIE D'EMPLOYEUR TRANCHE CE QUE LA CP LAISSE OUVERT (25/08/2026).
 *
 * Dix-neuf CP sur les 95 que l'ONSS couvre ont un taux qui DÉPEND de la
 * catégorie d'employeur, et l'écart n'est pas cosmétique — mesuré sur le
 * fichier des taux 2026/3 :
 *   CP 145 horticulture, ouvrier : 0,10 % (cat. 039) … 12,53 % (cat. 194)
 *   CP 323 gestion d'immeubles  : 0,20 % (cat. 037) … 19,40 % (cat. 112/113)
 *   CP 144 agriculture, ouvrier : 0,10 % (cat. 039) … 10,58 % (cat. 193)
 *   CP 140 transport, ouvrier   : 0,87 % (cat. 283) …  7,70 % (cat. 083)
 *
 * Ces CP-là portaient donc, faute de pouvoir trancher, un nombre rond jamais
 * sourcé (0,04 · 0,05 · 0,077 · 0,02…). **Le manque n'était pas de sourcing :
 * la réponse est publiée, elle attend juste qu'on lui dise QUI demande.**
 *
 * D'où la règle ici : dès que le dossier porte une catégorie d'employeur
 * utilisable ET que l'ONSS la rattache à cette CP, c'est le percepteur qui
 * répond, pas notre nombre rond. Sinon rien ne change, et on le DIT
 * (`categorieRequise`) pour que l'écran réclame le champ au lieu de servir un
 * taux tiré au sort.
 *
 * ⚠️ Le 210 qui a fui en base ne compte pas comme une catégorie — c'est celle
 * d'Aureus, et `categorie-employeur.js` existe pour ça depuis le 17/08.
 *
 * Le taux patronal applicable, et sur quelle base.
 * @param {object|null} fondsSE  le bloc `TAUX_SECTORIELS[cp].fondsSE`
 * @param {{ouvrier?: boolean, cp?: string, categorieOnss?: string|number, estDossierAureus?: boolean}} options
 * @returns {{taux:number, base108:boolean, coeffAssiette:number, public:string, sourceOnss:boolean,
 *            origineTaux:'fiche'|'onss-categorie', categorieOnss:string|null,
 *            categorieRequise:boolean, categorieNonRattachee:boolean, ambigu:Array|null}}
 */
export function tauxFondsSE(fondsSE, { ouvrier = false, cp = null, categorieOnss = null, estDossierAureus = false } = {}) {
  const f = fondsSE || null;
  const sourceOnss = Boolean(f?.onssSourcee);
  const pub = ouvrier ? 'ouvrier' : 'employe';

  // ── LES COMMISSIONS D'OUVRIERS N'ONT PAS D'EMPLOYÉS ─────────────────────────
  // Ajouté le 25/08/2026. Les CP 119, 149.03, 149.04, 152.02 et 225.02 sont des
  // commissions d'OUVRIERS : les employés du même employeur relèvent d'une autre
  // CP (200, 201 ou 202 selon le cas), et c'est l'ONSS qui le dit dans la
  // description de la catégorie d'employeur. Leur cotisation se calcule donc sur
  // la fiche de LEUR CP, pas sur celle-ci.
  // Servir le taux ouvrier à un employé le multiplierait par dix à vingt ; servir
  // un taux moyen serait l'inventer. On rend 0 et on nomme les CP de renvoi.
  // ── ET LE MIROIR : LES COMMISSIONS D'EMPLOYÉS N'ONT PAS D'OUVRIERS ──────────
  // Ajouté le 25/08/2026. Les CP 220 (employés de l'industrie alimentaire) et
  // 226 (employés du commerce international) sont des commissions d'EMPLOYÉS :
  // les ouvriers du même employeur relèvent d'une autre CP — 118 pour la
  // première, 140 pour la seconde — et c'est l'ONSS qui le dit dans la
  // description de la catégorie d'employeur. Les taux « ouvriers » que l'index
  // rattache à ces CP sont ceux du fonds de l'AUTRE commission : les servir ici
  // facturerait 1,47 % (CP 118) ou 7,70 % (CP 140) à un employé.
  if (ouvrier && Array.isArray(f?.ouvriersRelevantDUneAutreCp) && f.ouvriersRelevantDUneAutreCp.length) {
    return {
      taux: 0, base108: false, coeffAssiette: 1, public: pub, sourceOnss,
      ouvriersHorsCp: f.ouvriersRelevantDUneAutreCp,
      origineTaux: 'fiche', categorieOnss: null, categorieRequise: false, categorieNonRattachee: false, ambigu: null,
    };
  }
  if (!ouvrier && Array.isArray(f?.employesRelevantDUneAutreCp) && f.employesRelevantDUneAutreCp.length) {
    return {
      taux: 0, base108: false, coeffAssiette: 1, public: pub, sourceOnss,
      employesHorsCp: f.employesRelevantDUneAutreCp,
      origineTaux: 'fiche', categorieOnss: null, categorieRequise: false, categorieNonRattachee: false, ambigu: null,
    };
  }
  // ⚠️ LE GATE DU TAUX EMPLOYÉ ÉTAIT TROP ÉTROIT — corrigé le 26/08/2026.
  // Il n'honorait `taux_patronal_employe` que sur les fiches `onssSourcee`,
  // parce que ce champ est né avec le branchement de l'ONSS. Mais une CCT qui
  // écrit « 0,44 p.c. pour les travailleurs sous statut employé » est une
  // source au même titre que le fichier des taux du percepteur. Sans cette
  // ouverture, la CP 318.01 servait aux employés le 1,08 % des ouvriers —
  // deux fois et demie le montant dû.
  // ⛔ Le gate reste FERMÉ aux fiches sans source : sur un nombre rond, un
  // second taux serait un second nombre rond.
  const publicSource = Boolean(sourceOnss || f?.cctSourcee);
  const tauxFiche = (publicSource && !ouvrier && typeof f.taux_patronal_employe === 'number')
    ? f.taux_patronal_employe
    : (Number(f?.taux_patronal) || 0);
  const socle = {
    taux: tauxFiche,
    base108: baseA108(f, ouvrier, sourceOnss),
    coeffAssiette: coeffDAssiette(f, ouvrier, sourceOnss),
    public: pub,
    sourceOnss,
    origineTaux: 'fiche',
    categorieOnss: null,
    categorieRequise: false,
    categorieNonRattachee: false,
    ambigu: null,
  };
  if (!cp) return socle;

  // ⚠️ LA SOUS-CP D'ABORD, LA MÈRE ENSUITE — et ce n'est PAS le même arbitrage
  // que celui du moteur de paie. Là-bas, descendre à la sous-CP décide quelle
  // FICHE MAISON lire, et le garde-fou existe parce que la plupart de ces
  // fiches portent des nombres ronds non sourcés. Ici on interroge l'ONSS sur
  // la CP réelle du travailleur : il n'y a rien à arbitrer, il faut juste poser
  // la question à la bonne adresse. L'index ONSS connaît « 149.01 » et pas
  // « 149 » ; tronquer ferait manquer la réponse au lieu de la protéger.
  const cpOnss = cleOnssPourCp(cp);
  if (!cpOnss) return socle;

  // La CP est-elle de celles dont le taux dépend de la catégorie ? Si non, la
  // catégorie n'apporte rien et on ne change rien — inutile de créer un second
  // chemin de calcul pour les 76 CP qui n'en ont pas besoin.
  const ouvert = tauxPourcentage(cpOnss, { public: pub });
  const ambigu = ouvert?.ambigu ?? null;

  const utilisable = categorieEmployeurUtilisable(categorieOnss, estDossierAureus);
  if (!utilisable.ecrire) {
    // Pas de catégorie exploitable. On garde le taux de la fiche — mais si la
    // CP est ambiguë, ce taux est un pari : l'écran doit le dire.
    return { ...socle, categorieRequise: Boolean(ambigu), ambigu };
  }

  const parCat = tauxPourCategorie(cpOnss, utilisable.ecrire, { public: pub });
  if (!parCat) return { ...socle, categorieRequise: Boolean(ambigu), ambigu };
  if (parCat.connue === false) {
    // ⚠️ « Non rattachée » ≠ « fausse ». La carte catégorie → CP se lit dans la
    // description de l'annexe 27, qui ne cite pas toujours sa commission
    // paritaire : 143 des 156 catégories porteuses d'une cotisation FSE y sont
    // rattachées, 13 n'y sont pas. On ne s'en sert donc pas — mais on ne
    // décrète pas le dossier faux (R2 ter : « je n'ai pas trouvé » n'est pas
    // « ça n'existe pas »).
    //
    // Et on ne le SIGNALE que si la catégorie changeait quelque chose : sur une
    // CP non ambiguë le taux de la fiche répond déjà, et lever un drapeau là
    // serait du bruit — le fondateur irait vérifier un champ qui va bien.
    return {
      ...socle,
      categorieOnss: parCat.categorie,
      categorieNonRattachee: Boolean(ambigu),
      categorieRequise: Boolean(ambigu),
      ambigu,
    };
  }
  return {
    ...socle,
    taux: Number(parCat.taux) || 0,
    // Le code 820 est dû « sur les rémunérations à 108 % des travailleurs
    // manuels » — c'est l'intitulé ONSS lui-même (annexe 2), et le taux ouvrier
    // rendu ici ne vient que de ce code. La base suit donc le taux.
    base108: Boolean(ouvrier),
    // ⚠️ ET LE COEFFICIENT SUIT AUSSI. Sans cette ligne, une fiche qui déclare
    // une assiette maison (CP 324 : 110 %) l'imposerait au taux du percepteur,
    // qui se calcule sur 108 % — le socle est hérité par `...socle`. Quand
    // c'est l'ONSS qui répond, c'est l'assiette de l'ONSS qui s'applique.
    coeffAssiette: ouvrier ? COEFF_OUVRIER : 1,
    origineTaux: 'onss-categorie',
    categorieOnss: parCat.categorie,
    ambigu,
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// LE SECOND PILIER SECTORIEL — 42 COUPLES CP × PUBLIC QUE RIEN NE COMPTAIT
//
// MESURE DU 25/08/2026, AVEC SON DÉNOMINATEUR. L'ONSS perçoit, sous les codes
// travailleur 825 (ouvriers) et 835 (employés), un fonds de PENSION sectoriel
// — un second pilier. Il est dû par l'employeur, il est publié, et il
// n'entrait dans AUCUN calcul de coût : `cotisationFondsSE` ne lit que
// `taux_patronal`, qui est le fonds de sécurité d'existence.
//
// 42 couples CP × public sont concernés, de 0,48 % (CP 318.02) à 4,95 %
// (CP 216, employés). Sur un brut de 3 000 €, la CP 216 c'est 148,50 € par
// mois et par travailleur, absents du coût annoncé.
//
// ⛔ POURQUOI IL NE VA PAS DANS `taux_patronal`. Un second pilier n'est pas un
// fonds de sécurité d'existence, et `taux_patronal` s'affiche partout sous
// cette étiquette. Les additionner rendrait le total juste et le libellé faux
// — la faute que R2 ter-B nomme : une donnée juste plus un affichage menteur
// donnent un menteur.
//
// ⛔ ET POURQUOI ON NE L'AJOUTE PAS PARTOUT. Le risque est le DOUBLE COMPTAGE :
// un taux codé à la main peut déjà agréger la pension. On ne l'ajoute donc que
// lorsqu'on peut PROUVER que le taux du fonds l'exclut. Trois preuves admises,
// et rien d'autre :
//   · `onssSourcee` — le taux vient des codes 820/830 du fichier ONSS, qui par
//     construction ne contiennent pas les codes 825/835 ;
//   · `ecartOnssExplique` — la composition du code 820 est écrite et retombe
//     sur le montant perçu (CP 149.01 : 1,10 + 0,80 + 13,15 = 15,05) ;
//   · `pensionExclueDuTaux` — la convention le dit elle-même (CP 127 : « 15,7 %
//     […] y exclus les 3 % réservés pour le Fonds de Pension »).
// ✅ MESURE REFAITE LE 05/09/2026 — la ligne ci-dessus (38/42, CP 152/220/226/323
// en reste) était PÉRIMÉE (R2 ter : une note d'indisponibilité se re-teste avant
// d'être crue). Vérifié en direct via `pensionSectorielleOnss()` : les fiches
// 152.01, 152.02, 220 et 226 ont depuis été sourcées à l'ONSS (`onssSourcee`
// posé sur leur `fondsSE`) — le gate passe déjà pour elles, sans rien à changer
// ici. Seule la CP 323 reste réellement bloquée : son `fondsSE.taux_patronal`
// (2,00 %, `aVerifier: true`) ne peut toujours pas prouver qu'il exclut la
// pension — 18,20 points restent inexpliqués entre les 1,20 % lus dans les CCT
// et les 19,40 % perçus par l'ONSS côté ouvrier (détail dans la fiche 323
// elle-même). Sur les couples CP × public qui portent une ligne de pension
// ONSS (825/835) avec un taux positif, la mesure du 05/09/2026 en trouve 40
// prouvées et seulement CP 323 (ouvrier ET employé) en reste — le dénominateur
// exact diffère du « 42 » du 25/08 parce que ce chiffre comptait les couples
// différemment (par CP root plutôt que par CP × public réellement concerné) ;
// à re-clarifier si quelqu'un doit re-dériver ce total un jour.
//
// 📐 LA BASE EST LE BRUT NU, PAS 108 %. L'intitulé ONSS du code 820 dit
// explicitement « sur les rémunérations à 108 % » ; celui du code 825 ne le dit
// PAS. Et les deux conventions de second pilier lues au texte primaire le
// calculent sur le brut : CP 318.02 « du montant brut des rémunérations, avant
// retenue des cotisations personnelles de sécurité sociale » (CCT du
// 05/02/2026, art. 2) et CP 102.01 « 1 p.c. de la masse salariale » (CCT du
// 11/12/2019, art. 56). Prendre 108 % gonflerait chaque montant de 8 % sur une
// supposition — c'est exactement ce que R2 bis interdit.
// ✅ CORRIGÉ le 05/09/2026 — la note « page inaccessible » ci-dessous était
// PÉRIMÉE (R2 ter : une note d'indisponibilité se re-teste avant d'être crue).
// La page ONSS a été relue avec succès aujourd'hui :
// https://www.socialsecurity.be/employer/instructions/dmfa/fr/latest/instructions/special_contributions/other_specialcontributions/subsistenceprotectionfunds.html
// ✅ RÉSOLU LE 05/09/2026 — CE N'ÉTAIT PAS UNE VRAIE TENSION, PLUTÔT UNE PHRASE
// GÉNÉRALE À NE PAS SURINTERPRÉTER. Cette page décrit la base du code 825
// comme « un pourcentage du salaire brut, porté à 108 % pour les ouvriers » —
// c'est la convention DmfA COURANTE (celle que `baseA108` détecte déjà via
// `assiette`/`base` sur 6 fiches, cf. plus haut), pas une règle universelle qui
// écraserait le texte d'une CCT sectorielle explicite. Les deux CCT lues au
// texte primaire (CP 318.02, art. 2 ; CP 102.01, art. 56) fixent elles-mêmes
// leur propre assiette à « brut nu, avant retenues » sans mention de 108 % —
// c'est le texte CONSTITUTIF de leur fonds respectif, celui que l'ONSS
// collecte ensuite via DmfA selon les termes que CE fonds a fixés. La page
// générale de l'ONSS décrit le cas fréquent ; elle ne prive pas une CCT
// sectorielle du droit de fixer sa propre assiette autrement.
// Cohérent avec le principe déjà codé dans `coeffDAssiette` ci-dessus : « ON NE
// DEVINE PAS CE COEFFICIENT DANS UNE PHRASE » — le calcul reste sur le brut nu
// par défaut, et ne passe à 108 % QUE quand le texte propre au fonds le dit
// explicitement. Aucun changement de code nécessaire : la méthode déjà en
// place (déclarer par fonds, jamais supposer un coefficient générique) était
// la bonne — ce commentaire corrige seulement le diagnostic, pas le calcul.
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Le taux de pension sectorielle que l'ONSS perçoit, quand on peut prouver que
 * le taux du fonds ne l'inclut pas déjà.
 *
 * @returns {{taux:number, base108:boolean, public:string, prouve:boolean,
 *            raison:string|null, categorieOnss:string|null}}
 */
export function pensionSectorielleOnss(fondsSE, { ouvrier = false, cp = null, categorieOnss = null, estDossierAureus = false } = {}) {
  const pub = ouvrier ? 'ouvrier' : 'employe';
  const rien = { taux: 0, base108: false, public: pub, prouve: false, raison: null, categorieOnss: null };
  const f = fondsSE || null;
  if (!cp) return { ...rien, raison: 'CP inconnue' };

  const cpOnss = cleOnssPourCp(cp);
  if (!cpOnss) return { ...rien, raison: "l'ONSS ne publie rien pour cette commission paritaire" };

  // Le gate : sait-on que `taux_patronal` exclut la pension ?
  const exclut = Boolean(f?.onssSourcee || f?.ecartOnssExplique || f?.pensionExclueDuTaux);
  if (!exclut) {
    return {
      ...rien,
      raison: "le taux du fonds n'est pas sourcé : impossible de savoir s'il agrège déjà la pension sectorielle, donc on ne l'ajoute pas",
    };
  }

  const utilisable = categorieEmployeurUtilisable(categorieOnss, estDossierAureus);
  const o = utilisable.ecrire
    ? tauxPourCategorie(cpOnss, utilisable.ecrire, { public: pub })
    : tauxPourcentage(cpOnss, { public: pub });
  if (!o || o.connue === false || o.ambigu) {
    return { ...rien, raison: o?.ambigu ? "les catégories d'employeur ne s'accordent pas sur la pension" : 'aucune ligne lisible' };
  }
  const taux = Number(o.tauxPensionSectorielle) || 0;
  if (!taux) return { ...rien, prouve: true, raison: 'aucune pension sectorielle pour ce public' };
  return { taux, base108: false, public: pub, prouve: true, raison: null, categorieOnss: o.categorie ?? null };
}

/** Le montant de la pension sectorielle, arrondi à deux décimales. */
export function cotisationPensionSectorielle(fondsSE, brutMensuel, options = {}) {
  const brut = Number(brutMensuel) || 0;
  if (brut <= 0) return 0;
  const { taux } = pensionSectorielleOnss(fondsSE, options);
  if (!taux) return 0;
  return r2(brut * taux);
}

/**
 * Le montant de la cotisation, arrondi à deux décimales.
 * Rend 0 sur un brut nul ou négatif, et sur une fiche sans fonds — jamais NaN.
 */
export function cotisationFondsSE(fondsSE, brutMensuel, options = {}) {
  const brut = Number(brutMensuel) || 0;
  if (brut <= 0) return 0;
  const { taux, coeffAssiette } = tauxFondsSE(fondsSE, options);
  if (!taux) return 0;
  const coeff = Number(coeffAssiette) || 1;
  return r2((coeff === 1 ? brut : r2(brut * coeff)) * taux);
}
